import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Optional, Inject, Pipe, EventEmitter, Component, ChangeDetectionStrategy, Input, Output, NgModule } from '@angular/core';
import { BehaviorSubject, of, combineLatest } from 'rxjs';
import { filter, distinctUntilChanged, map, withLatestFrom, first, tap } from 'rxjs/operators';
import * as i1 from '@titan/devkit/config';
import * as i2$1 from '@titan/components/sidebar';
import * as i3$1 from '@ngrx/component';
import { PushPipe, LetDirective } from '@ngrx/component';
import * as i1$1 from '@angular/material/divider';
import { MatDividerModule } from '@angular/material/divider';
import * as i2 from '@angular/material/form-field';
import * as i3 from '@angular/material/select';
import { MatSelectModule } from '@angular/material/select';
import * as i4 from '@angular/material/core';
import * as i5 from '@angular/material/menu';
import { MatMenuModule } from '@angular/material/menu';
import * as i6 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';
import * as i7 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i8 from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import * as i5$1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

const ZONE_SELECTOR_CONFIG = new InjectionToken('Zone Selector Config');

/** Comparer to be used to speed up sorting (instead of `localeCompare`) */
const comparer = new Intl.Collator('en-US', {
    numeric: true,
    sensitivity: 'variant',
    caseFirst: 'lower',
});
const sortStr = comparer.compare;

class ZoneSelectorFacade {
    constructor(config, zoneSelectorConfig) {
        this.config = config;
        this.zoneSelectorConfig = zoneSelectorConfig;
        this._localStorageZoneKey = 'last-selected-zone';
        this._localStorageZone$ = this.getLocalStorageZone();
        this._selectedZone$ = new BehaviorSubject(undefined);
        this._availableZones$ = new BehaviorSubject([]);
        this._showZoneSelector$ = new BehaviorSubject(false);
        this.availableZones$ = this._availableZones$.asObservable();
        this.showZoneSelector$ = this._showZoneSelector$.asObservable();
        this.selectedZone$ = this._selectedZone$.asObservable().pipe(filter(zone => zone !== undefined), distinctUntilChanged());
        this._showZoneSelector$.next(zoneSelectorConfig.enableAllZonesSelector);
        this.config.zoneInit$
            .pipe(filter(r => !!r), map(() => [...this.config.zones].sort((a, b) => sortStr(a, b))))
            .subscribe(this._availableZones$);
        /**
         * Selected Zone is the first available zone on init if no zone saved in local storage
         * or local storage zone not in all zones
         */
        this.availableZones$
            .pipe(filter(zones => zones.length > 0), withLatestFrom(this._selectedZone$, this._localStorageZone$), filter(([zones, selectedZone, localStorageZone]) => selectedZone === undefined && !this.isInAvailableZones(zones, localStorageZone)), map(([zones]) => zones), map(([firstZoneForNow]) => firstZoneForNow))
            .subscribe(this._selectedZone$);
        /**
         * Selected Zone will use localStorage saved zone on init if it exists and is within all zones
         */
        this.availableZones$
            .pipe(filter(zones => zones.length > 0), withLatestFrom(this._selectedZone$, this._localStorageZone$), filter(([zones, selectedZone, localStorageZone]) => selectedZone === undefined && this.isInAvailableZones(zones, localStorageZone)), map(([, , localStorageZone]) => localStorageZone))
            .subscribe(this._selectedZone$);
    }
    set enableZoneSelector(enable) {
        this._showZoneSelector$.next(enable);
    }
    isInAvailableZones(availableZones, zone) {
        const allZonesEnabled = this.zoneSelectorConfig.enableAllZonesSelector;
        if (zone === '*') {
            return allZonesEnabled;
        }
        return availableZones.includes(zone ?? '');
    }
    /**
     * gets selectedZone from localStorage
     */
    getLocalStorageZone() {
        return of(window.localStorage.getItem(this._localStorageZoneKey)).pipe(map(zone => zone ?? undefined), map(zone => (zone === '*' && !this.zoneSelectorConfig.enableAllZonesSelector ? undefined : zone)));
    }
    /**
     * sets localStorage zone
     * @param zone
     */
    setLocalStorageZone(zone) {
        window.localStorage.setItem(this._localStorageZoneKey, zone);
    }
    /**
     * Ensures that the zone is actually selectable by validating the zone exists in the zones call from Daimyo.
     * If the user tries to select * (AKA: All Zones) and the developer has turned off All Zones, the result will be false.
     * Result will not return the result until the zones are available via the ConfigService
     */
    canSelectZone(zone) {
        if (zone === '*') {
            return of(this.zoneSelectorConfig.enableAllZonesSelector);
        }
        return combineLatest([this.config.zoneInit$, this.availableZones$]).pipe(filter(([ready, zones]) => ready && zones.length > 0), map(([, zones]) => zones.includes(zone)), first());
    }
    /**
     * Request that the daimyo zone be changed. This method will first validate if the zone requested is actually selectable.
     * If the zone is selectable, it will select the zone. However, if the zone is not selectable, it will do nothing.
     */
    selectZone(zone) {
        this.canSelectZone(zone)
            .pipe(filter(r => !!r), withLatestFrom(this._selectedZone$), filter(([, currentSelectedZone]) => currentSelectedZone !== zone), tap(() => {
            this.setLocalStorageZone(zone);
        }))
            .subscribe(() => {
            this._selectedZone$.next(zone);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZoneSelectorFacade, deps: [{ token: i1.TitanConfigService, optional: true }, { token: ZONE_SELECTOR_CONFIG }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZoneSelectorFacade }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZoneSelectorFacade, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.TitanConfigService, decorators: [{
                    type: Optional
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ZONE_SELECTOR_CONFIG]
                }] }] });

class ZoneToLocalePipe {
    transform(value) {
        if (value === '*') {
            return 'APP_SHELL.ALL_ZONES';
        }
        return value || '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZoneToLocalePipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.0.2", ngImport: i0, type: ZoneToLocalePipe, name: "zoneToLocale" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZoneToLocalePipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'zoneToLocale',
                }]
        }] });

class ZoneSelectComponent {
    constructor() {
        this.width = 'inherit';
        this.zoneSelected = new EventEmitter();
    }
    onZoneOptionSelect(e) {
        this.zoneSelected.next(e.value);
    }
    onZoneButtonSelect(zone) {
        this.zoneSelected.next(zone);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZoneSelectComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.0.2", type: ZoneSelectComponent, selector: "titan-zone-select", inputs: { zones: "zones", selectedZone: "selectedZone", showAllZonesSelector: "showAllZonesSelector", isCollapsed: "isCollapsed", width: "width" }, outputs: { zoneSelected: "zoneSelected" }, ngImport: i0, template: "@if (!isCollapsed) {\n\n<mat-form-field [style.width]=\"width\">\n    <mat-label>{{ (selectedZone | zoneToLocale | translate) || 'APP_SHELL.ZONES' }}</mat-label>\n    <mat-select (selectionChange)=\"onZoneOptionSelect($event)\">\n        @for (zone of zones; track zone) {\n        <mat-option [value]=\"zone\">{{ zone }}</mat-option>\n        } @if (showAllZonesSelector) {\n\n        <mat-divider></mat-divider>\n        <mat-option [value]=\"'*'\">{{ 'APP_SHELL.ALL_ZONES' | translate }}</mat-option>\n\n        }\n    </mat-select>\n</mat-form-field>\n\n} @else {\n\n<button\n    mat-icon-button\n    aria-label=\"Select Zone\"\n    [matMenuTriggerFor]=\"zoneMenu\"\n    [matMenuTriggerData]=\"{ zones, allZones: showAllZonesSelector }\">\n    <mat-icon>language</mat-icon>\n</button>\n<mat-menu #zoneMenu=\"matMenu\">\n    <ng-template matMenuContent let-zones=\"zones\" , let-allZones=\"allZones\">\n        @for (zone of zones; track zone) {\n        <button (click)=\"onZoneButtonSelect(zone)\" mat-menu-item>{{ zone }}</button>\n        } @if (allZones) {\n\n        <mat-divider></mat-divider>\n        <button mat-menu-item (click)=\"onZoneButtonSelect('*')\">{{ 'APP_SHELL.ALL_ZONES' | translate }}</button>\n\n        }\n    </ng-template>\n</mat-menu>\n\n}\n", styles: ["mat-form-field{min-width:inherit}\n"], dependencies: [{ kind: "component", type: i1$1.MatDivider, selector: "mat-divider", inputs: ["vertical", "inset"] }, { kind: "component", type: i2.MatFormField, selector: "mat-form-field", inputs: ["hideRequiredMarker", "color", "floatLabel", "appearance", "subscriptSizing", "hintLabel"], exportAs: ["matFormField"] }, { kind: "directive", type: i2.MatLabel, selector: "mat-label" }, { kind: "component", type: i3.MatSelect, selector: "mat-select", inputs: ["disabled", "disableRipple", "tabIndex", "aria-describedby", "panelClass", "hideSingleSelectionIndicator", "placeholder", "required", "multiple", "disableOptionCentering", "compareWith", "value", "aria-label", "aria-labelledby", "errorStateMatcher", "typeaheadDebounceInterval", "sortComparator", "id", "panelWidth"], outputs: ["openedChange", "opened", "closed", "selectionChange", "valueChange"], exportAs: ["matSelect"] }, { kind: "component", type: i4.MatOption, selector: "mat-option", inputs: ["value", "id", "disabled"], outputs: ["onSelectionChange"], exportAs: ["matOption"] }, { kind: "component", type: i5.MatMenu, selector: "mat-menu", inputs: ["backdropClass", "aria-label", "aria-labelledby", "aria-describedby", "xPosition", "yPosition", "overlapTrigger", "hasBackdrop", "class", "classList"], outputs: ["closed", "close"], exportAs: ["matMenu"] }, { kind: "component", type: i5.MatMenuItem, selector: "[mat-menu-item]", inputs: ["role", "disabled", "disableRipple"], exportAs: ["matMenuItem"] }, { kind: "directive", type: i5.MatMenuContent, selector: "ng-template[matMenuContent]" }, { kind: "directive", type: i5.MatMenuTrigger, selector: "[mat-menu-trigger-for], [matMenuTriggerFor]", inputs: ["mat-menu-trigger-for", "matMenuTriggerFor", "matMenuTriggerData", "matMenuTriggerRestoreFocus"], outputs: ["menuOpened", "onMenuOpen", "menuClosed", "onMenuClose"], exportAs: ["matMenuTrigger"] }, { kind: "component", type: i6.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }, { kind: "component", type: i7.MatIconButton, selector: "button[mat-icon-button]", exportAs: ["matButton"] }, { kind: "pipe", type: i8.TranslatePipe, name: "translate" }, { kind: "pipe", type: ZoneToLocalePipe, name: "zoneToLocale" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZoneSelectComponent, decorators: [{
            type: Component,
            args: [{ selector: 'titan-zone-select', changeDetection: ChangeDetectionStrategy.OnPush, template: "@if (!isCollapsed) {\n\n<mat-form-field [style.width]=\"width\">\n    <mat-label>{{ (selectedZone | zoneToLocale | translate) || 'APP_SHELL.ZONES' }}</mat-label>\n    <mat-select (selectionChange)=\"onZoneOptionSelect($event)\">\n        @for (zone of zones; track zone) {\n        <mat-option [value]=\"zone\">{{ zone }}</mat-option>\n        } @if (showAllZonesSelector) {\n\n        <mat-divider></mat-divider>\n        <mat-option [value]=\"'*'\">{{ 'APP_SHELL.ALL_ZONES' | translate }}</mat-option>\n\n        }\n    </mat-select>\n</mat-form-field>\n\n} @else {\n\n<button\n    mat-icon-button\n    aria-label=\"Select Zone\"\n    [matMenuTriggerFor]=\"zoneMenu\"\n    [matMenuTriggerData]=\"{ zones, allZones: showAllZonesSelector }\">\n    <mat-icon>language</mat-icon>\n</button>\n<mat-menu #zoneMenu=\"matMenu\">\n    <ng-template matMenuContent let-zones=\"zones\" , let-allZones=\"allZones\">\n        @for (zone of zones; track zone) {\n        <button (click)=\"onZoneButtonSelect(zone)\" mat-menu-item>{{ zone }}</button>\n        } @if (allZones) {\n\n        <mat-divider></mat-divider>\n        <button mat-menu-item (click)=\"onZoneButtonSelect('*')\">{{ 'APP_SHELL.ALL_ZONES' | translate }}</button>\n\n        }\n    </ng-template>\n</mat-menu>\n\n}\n", styles: ["mat-form-field{min-width:inherit}\n"] }]
        }], propDecorators: { zones: [{
                type: Input
            }], selectedZone: [{
                type: Input
            }], showAllZonesSelector: [{
                type: Input
            }], isCollapsed: [{
                type: Input
            }], width: [{
                type: Input
            }], zoneSelected: [{
                type: Output
            }] } });

class ZoneSelectorComponent {
    constructor(zoneSelectorConfig, zoneSelectorFacade, 
    // TODO: Use light-weight Injection Token https://angular.io/guide/lightweight-injection-tokens
    sidenavService) {
        this.zoneSelectorConfig = zoneSelectorConfig;
        this.zoneSelectorFacade = zoneSelectorFacade;
        this.sidenavService = sidenavService;
        this.zones$ = this.zoneSelectorFacade.availableZones$;
        this.selectedZone$ = this.zoneSelectorFacade.selectedZone$;
        this.showAllZonesSelector = this.zoneSelectorConfig.enableAllZonesSelector;
        this.isCollapsed = false;
        this.width = 'inherit';
        this.disableFadeinAnimation = false;
        if (!this.sidenavService) {
            return;
        }
    }
    zoneSelected(zone) {
        this.zoneSelectorFacade.selectZone(zone);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZoneSelectorComponent, deps: [{ token: ZONE_SELECTOR_CONFIG }, { token: ZoneSelectorFacade }, { token: i2$1.SidenavService, optional: true }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.0.2", type: ZoneSelectorComponent, selector: "titan-zone-selector", inputs: { width: "width", disableFadeinAnimation: "disableFadeinAnimation" }, ngImport: i0, template: "<ng-container *ngrxLet=\"zones$; let zones\">\n    <titan-zone-select\n        [width]=\"width\"\n        [isCollapsed]=\"(zoneSelectorConfig.enabledSidenavCollapse && (this.sidenavService.sidenavCollapsed$ | async)) || false\"\n        [zones]=\"zones\"\n        [selectedZone]=\"selectedZone$ | async\"\n        [showAllZonesSelector]=\"showAllZonesSelector\"\n        (zoneSelected)=\"zoneSelected($event)\"></titan-zone-select>\n</ng-container>\n", styles: ["@keyframes fadein{0%{opacity:0}to{opacity:1}}.fade-in{animation:fadein .35s ease-in-out}titan-zone-select{min-width:inherit}\n"], dependencies: [{ kind: "directive", type: i3$1.LetDirective, selector: "[ngrxLet]", inputs: ["ngrxLet", "ngrxLetSuspenseTpl"] }, { kind: "component", type: ZoneSelectComponent, selector: "titan-zone-select", inputs: ["zones", "selectedZone", "showAllZonesSelector", "isCollapsed", "width"], outputs: ["zoneSelected"] }, { kind: "pipe", type: i5$1.AsyncPipe, name: "async" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: ZoneSelectorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'titan-zone-selector', changeDetection: ChangeDetectionStrategy.OnPush, template: "<ng-container *ngrxLet=\"zones$; let zones\">\n    <titan-zone-select\n        [width]=\"width\"\n        [isCollapsed]=\"(zoneSelectorConfig.enabledSidenavCollapse && (this.sidenavService.sidenavCollapsed$ | async)) || false\"\n        [zones]=\"zones\"\n        [selectedZone]=\"selectedZone$ | async\"\n        [showAllZonesSelector]=\"showAllZonesSelector\"\n        (zoneSelected)=\"zoneSelected($event)\"></titan-zone-select>\n</ng-container>\n", styles: ["@keyframes fadein{0%{opacity:0}to{opacity:1}}.fade-in{animation:fadein .35s ease-in-out}titan-zone-select{min-width:inherit}\n"] }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [ZONE_SELECTOR_CONFIG]
                }] }, { type: ZoneSelectorFacade }, { type: i2$1.SidenavService, decorators: [{
                    type: Optional
                }] }], propDecorators: { width: [{
                type: Input
            }], disableFadeinAnimation: [{
                type: Input
            }] } });

/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable  @typescript-eslint/no-empty-function */
class TitanZoneSelectorModule {
    static forRoot(config = { enableAllZonesSelector: false }) {
        return {
            ngModule: TitanZoneSelectorModule,
            providers: [
                {
                    provide: ZONE_SELECTOR_CONFIG,
                    useValue: config,
                },
                ZoneSelectorFacade,
            ],
        };
    }
    constructor(zoneSelectorFacade) { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanZoneSelectorModule, deps: [{ token: ZoneSelectorFacade, optional: true }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.0.2", ngImport: i0, type: TitanZoneSelectorModule, declarations: [ZoneSelectorComponent, ZoneSelectComponent, ZoneToLocalePipe], imports: [CommonModule,
            FormsModule,
            PushPipe,
            TranslateModule,
            LetDirective,
            MatDividerModule,
            MatSelectModule,
            LetDirective,
            MatMenuModule,
            MatIconModule,
            MatButtonModule], exports: [ZoneSelectorComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanZoneSelectorModule, imports: [CommonModule,
            FormsModule,
            TranslateModule,
            MatDividerModule,
            MatSelectModule,
            MatMenuModule,
            MatIconModule,
            MatButtonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.0.2", ngImport: i0, type: TitanZoneSelectorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        FormsModule,
                        PushPipe,
                        TranslateModule,
                        LetDirective,
                        MatDividerModule,
                        MatSelectModule,
                        LetDirective,
                        MatMenuModule,
                        MatIconModule,
                        MatButtonModule,
                    ],
                    declarations: [ZoneSelectorComponent, ZoneSelectComponent, ZoneToLocalePipe],
                    exports: [ZoneSelectorComponent],
                }]
        }], ctorParameters: () => [{ type: ZoneSelectorFacade, decorators: [{
                    type: Optional
                }] }] });

/**
 * Generated bundle index. Do not edit.
 */

export { TitanZoneSelectorModule, ZoneSelectorComponent, ZoneSelectorFacade };
